import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBooking, useCancelBooking } from '@/api/hooks/useBookings';
import { formatCurrency, formatLocalTime } from '@/lib/format';
import type { BookingStatus } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import UnlockDialog from '@/components/booking/UnlockDialog';
import PriceBreakdown from '@/components/booking/PriceBreakdown';
import { ChevronLeft, ChevronDown, ChevronUp, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS: Record<BookingStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-green-100 text-green-800',
  ACTIVE: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-gray-100 text-gray-600',
  CANCELLED: 'bg-red-100 text-red-800',
  NO_SHOW: 'bg-orange-100 text-orange-800',
  EXPIRED: 'bg-gray-100 text-gray-500',
};

// Default cancellation tiers (matches seed data / platform default)
const DEFAULT_TIERS = [
  { hoursBeforeSession: 24, refundPercent: 100 },
  { hoursBeforeSession: 4, refundPercent: 50 },
  { hoursBeforeSession: 0, refundPercent: 0 },
];
const CREDIT_BONUS_PERCENT = 0;

function getRefundTier(startTime: string, tiers = DEFAULT_TIERS) {
  const hoursUntil = (new Date(startTime).getTime() - Date.now()) / (1000 * 60 * 60);
  // Tiers sorted by hoursBeforeSession descending — find first where hoursUntil >= tier.hours
  const sorted = [...tiers].sort((a, b) => b.hoursBeforeSession - a.hoursBeforeSession);
  for (const tier of sorted) {
    if (hoursUntil >= tier.hoursBeforeSession) {
      return tier;
    }
  }
  return sorted[sorted.length - 1]; // Fallback to smallest tier (0% refund)
}

export default function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useBooking(id || '');
  const cancelMutation = useCancelBooking();
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [refundMethod, setRefundMethod] = useState<string>('ORIGINAL_PAYMENT');
  const [showBreakdown, setShowBreakdown] = useState(false);

  const refundInfo = useMemo(() => {
    if (!data) return null;
    const { booking } = data;
    const tier = getRefundTier(booking.startTime);
    const total = booking.pricing.total;
    const currency = booking.pricing.currency;
    const refundAmount = Math.round(total * (tier.refundPercent / 100) * 100) / 100;
    const creditAmount = Math.round(refundAmount * (1 + CREDIT_BONUS_PERCENT / 100) * 100) / 100;
    return {
      refundPercent: tier.refundPercent,
      refundAmount,
      creditAmount,
      total,
      currency,
      noRefundHours: tier.refundPercent === 0
        ? DEFAULT_TIERS.find((t) => t.refundPercent > 0)?.hoursBeforeSession || 4
        : 0,
    };
  }, [data]);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-md space-y-6 px-4 py-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-md px-4 py-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {t('common.error')}
        </div>
      </div>
    );
  }

  const { booking } = data;
  const access = booking.access;
  const canCancel = booking.status === 'CONFIRMED';
  const isActive = booking.status === 'ACTIVE';

  const handleCancel = () => {
    cancelMutation.mutate(
      { id: booking.id, refundMethod },
      {
        onSuccess: () => {
          setCancelDialogOpen(false);
          if (refundInfo) {
            const amount = refundMethod === 'PLATFORM_CREDIT'
              ? formatCurrency(refundInfo.creditAmount, refundInfo.currency, i18n.language)
              : formatCurrency(refundInfo.refundAmount, refundInfo.currency, i18n.language);
            const key = refundMethod === 'PLATFORM_CREDIT'
              ? 'booking.cancel_success_credit'
              : 'booking.cancel_success_refund';
            toast.success(t(key, { amount }));
          } else {
            toast.success(t('booking.booking_cancelled'));
          }
        },
      },
    );
  };

  return (
    <div className="mx-auto max-w-md space-y-6 px-4 py-6">
      {/* Back */}
      <button
        onClick={() => navigate('/bookings')}
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        {t('booking.my_bookings')}
      </button>

      {/* Header with status */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('booking.booking_detail')}</h1>
        <Badge variant="secondary" className={STATUS_COLORS[booking.status]}>
          {t(`booking.status_${booking.status.toLowerCase()}`)}
        </Badge>
      </div>

      {/* Access card — for CONFIRMED/ACTIVE. Sprint 8 Day 1 keypad-UX
          reframe: the unlock CTA is the PRIMARY action, prominent at the
          top. The keypad code is presented below as a labelled backup —
          customers in low-signal venues read it off the screen and type
          at the door. Retry guidance ("wait ~10s and try again") sits
          below the code to reduce support pings on the next fumble. */}
      {access && (booking.status === 'CONFIRMED' || booking.status === 'ACTIVE') && (
        <Card className="border-2 border-primary bg-primary/5">
          <CardContent className="space-y-5 p-8">
            {access.code && (
              <UnlockDialog
                accessCode={access.code}
                bookingStartTime={booking.startTime}
                startTimeLocal={booking.startTimeLocal}
                unlockAvailableAt={access.unlockAvailableAt}
                unitName={booking.unitName}
              />
            )}
            <KeypadCodeFallback code={access.code} instructions={access.instructions} />
          </CardContent>
        </Card>
      )}

      {/* Booking summary — no labels */}
      <Card>
        <CardContent className="space-y-3 p-6 text-center">
          <p className="text-lg font-semibold">{t(`sport_${booking.sportCode}`, { defaultValue: booking.sportName })}</p>
          <p className="text-xl font-medium">
            {formatLocalTime(booking.startTimeLocal, i18n.language)} → {formatLocalTime(booking.endTimeLocal, i18n.language)}
          </p>
          <p className="text-muted-foreground">
            {new Date(booking.startTime).toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
          <p className="text-sm text-muted-foreground">
            {booking.unitName} · {booking.duration} min
            {booking.originalDuration && booking.originalDuration !== booking.duration && (
              <span className="text-xs ml-1">
                ({booking.originalDuration} min + {booking.duration - booking.originalDuration} min extended)
              </span>
            )}
            {booking.playerCount > 1 && ` · ${booking.playerCount} players`}
          </p>

          {/* Addon items */}
          {booking.pricing.addonItems?.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {booking.pricing.addonItems
                .map((item) => item.name + (item.quantity > 1 ? ` × ${item.quantity}` : ''))
                .join(', ')}
            </p>
          )}

          <p className="pt-2 text-xl font-mono font-semibold">
            {formatCurrency(booking.pricing.total, booking.pricing.currency, i18n.language)}
          </p>

          {/* Collapsible price breakdown */}
          <button
            onClick={() => setShowBreakdown(!showBreakdown)}
            className="flex items-center gap-1 text-sm text-primary hover:underline mx-auto pt-2"
          >
            {t('booking.view_price_breakdown')}
            {showBreakdown ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>

          {showBreakdown && (
            <PriceBreakdown booking={booking} />
          )}
        </CardContent>
      </Card>

      {/* Action buttons */}
      <div className="flex flex-col gap-3">
        {isActive && (
          <Button
            className="w-full whitespace-nowrap"
            onClick={() => navigate(`/session/${booking.id}`)}
          >
            {t('booking.go_to_session')}
          </Button>
        )}

        {canCancel && (
          <Button
            variant="destructive"
            className="w-full whitespace-nowrap"
            onClick={() => setCancelDialogOpen(true)}
          >
            {t('booking.cancel_booking')}
          </Button>
        )}
      </div>

      {/* Cancel confirmation dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('booking.cancel_confirm_title')}</DialogTitle>
            <DialogDescription>{t('booking.cancel_confirm_message')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Refund to original payment */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="refundMethod"
                value="ORIGINAL_PAYMENT"
                checked={refundMethod === 'ORIGINAL_PAYMENT'}
                onChange={() => setRefundMethod('ORIGINAL_PAYMENT')}
                className="accent-primary mt-1"
              />
              <div>
                <span className="text-sm font-medium">{t('booking.refund_original')}</span>
                {refundInfo && refundInfo.refundPercent > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('booking.refund_amount', {
                      amount: formatCurrency(refundInfo.refundAmount, refundInfo.currency, i18n.language),
                      percent: refundInfo.refundPercent,
                      total: formatCurrency(refundInfo.total, refundInfo.currency, i18n.language),
                    })}
                  </p>
                )}
              </div>
            </label>

            {/* Platform credit */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="refundMethod"
                value="PLATFORM_CREDIT"
                checked={refundMethod === 'PLATFORM_CREDIT'}
                onChange={() => setRefundMethod('PLATFORM_CREDIT')}
                className="accent-primary mt-1"
              />
              <div>
                <span className="text-sm font-medium">{t('booking.refund_credit')}</span>
                {refundInfo && refundInfo.refundPercent > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('booking.credit_amount', {
                      amount: formatCurrency(refundInfo.creditAmount, refundInfo.currency, i18n.language),
                    })}
                  </p>
                )}
              </div>
            </label>

            {/* No refund warning */}
            {refundInfo && refundInfo.refundPercent === 0 && (
              <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {t('booking.no_refund', { hours: refundInfo.noRefundHours })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
              {t('common.back')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('booking.cancel_booking')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Keypad-code fallback block shown on the booking detail page below the
 * primary "Tap to Unlock" CTA. Sprint 8 Day 1 keypad-UX reframe: the
 * TTLock M302 keypad has no display and no clear key, so customers who
 * fumble a digit have no in-hand feedback — they wait for timeout and
 * re-try. Big monospaced display with wide letter-spacing makes the code
 * easy to read at arm's length while standing at the door; the copy
 * button covers the offline-but-bringing-phone case (read once, copy
 * once, paste-or-type at the keypad even without signal).
 */
function KeypadCodeFallback({
  code,
  instructions,
}: {
  code: string | undefined;
  instructions: string | undefined;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!code) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success(t('booking.code_copied'));
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can be blocked (insecure context, denied permission).
      // Fall back to a noisy failure rather than a silent no-op — the
      // customer can still read the code off the screen.
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="rounded-md border border-primary/20 bg-background/60 p-4 text-center space-y-2">
      <p className="text-sm text-muted-foreground">
        {t('booking.keypad_backup_label')}
      </p>
      <div className="flex items-center justify-center gap-3">
        <p className="text-4xl font-mono font-bold tracking-[0.3em]">{code}</p>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleCopy}
          aria-label={t('booking.copy_code')}
          className="h-9 w-9 p-0"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('booking.keypad_retry_hint')}
      </p>
      {instructions && (
        <p className="text-xs text-muted-foreground italic">{instructions}</p>
      )}
    </div>
  );
}
